/**
 * A property the broker adds themselves, pinned on a map.
 *
 * Different from everything else the broker does here: the rest of the panel works on properties an
 * owner sent, where the owner described the place and the broker dresses it for the market. This one
 * is the broker's own entry, so it asks for the whole thing — and asks for the location as a pin
 * rather than a district name, which is the difference between "somewhere in النرجس" and an address.
 *
 * Creating it creates its draft listing too; publishing is the same button as any other listing. The
 * same form edits one already saved (`PATCH /api/broker/properties/:id` and its listing), and while a
 * new one is being entered it is kept in the browser, so a reload or a closed tab does not lose it.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Loader2, Pencil, Plus, Trash2, Upload, X } from 'lucide-react';
import { MAX_LISTING_PHOTOS, PROPERTY_PURPOSES, PROPERTY_TYPES, PROPERTY_TYPE_LABELS, FACADES, LAND_USES, RENT_PERIODS, TENANT_TYPES, type PropertyType } from '@wasity/shared';
import { actAs, api, uploadFile } from '@/lib/api';
import { useSession } from '@/lib/auth';
import { errorText, fieldErrors, specificErrorText } from '@/lib/errors';
import type { Listing } from '@/lib/types';
import { useI18n } from '@/i18n';
import { Button, Card, ConfirmDialog, Dialog, EmptyState, ErrorNotice, Field, Input, Notice, PageHeader, Select, Skeleton, Table, Textarea } from '@/components/ui';
import { ListingChip } from './Listings';
import { MapPicker, type LatLng } from '@/components/MapPicker';
import { DEFAULT_WINDOWS, ViewingWindows, windowsValid, type Window } from './ViewingWindows';
import { MembershipNeededNotice, usePublishAllowed } from './common';

const FEATURES = ['furnished', 'kitchen', 'maidRoom', 'driverRoom', 'elevator', 'pool', 'basement', 'garage', 'duplex', 'waterTank', 'water', 'electricity', 'drainage'] as const;
/** Rooms make no sense on land, and land use makes no sense on a villa. */
const ROOMED: PropertyType[] = ['apartment', 'villa', 'floor', 'building', 'rest_house', 'chalet'];
/** Anything with a structure on it. Land and farmland have no storeys, no floor number and no age. */
const BUILT: PropertyType[] = ['apartment', 'villa', 'floor', 'building', 'rest_house', 'chalet', 'shop', 'office', 'warehouse'];
/**
 * The same conditions `propertyInput` enforces (packages/shared/src/schemas.ts). Kept here so the form
 * says what is missing while it is being filled in, rather than letting the save go and come back
 * refused — the server is still the one that decides, this only stops a pointless round trip.
 */
const RESIDENTIAL: PropertyType[] = ['apartment', 'villa', 'floor', 'rest_house', 'chalet'];

interface BrokerProperty {
  id: string;
  type: PropertyType;
  purpose: string;
  city: string;
  district: string;
  street: string | null;
  lat: number;
  lng: number;
  areaM2: number;
  bedrooms: number | null;
  bathrooms: number | null;
  livingRooms: number | null;
  floors: number | null;
  floorNo: number | null;
  units: number | null;
  shops: number | null;
  landUse: string | null;
  ageYears: number | null;
  facade: string | null;
  streetWidthM: number | null;
  features: Record<string, boolean> | null;
  rentPeriod: string | null;
  tenantType: string | null;
  photoMediaIds: string[];
  listing: Listing | null;
}

const blank = {
  purpose: 'sell',
  type: 'villa' as PropertyType,
  city: 'الرياض',
  district: '',
  street: '',
  areaM2: '',
  bedrooms: '',
  bathrooms: '',
  livingRooms: '',
  floors: '',
  floorNo: '',
  units: '',
  shops: '',
  landUse: '',
  ageYears: '',
  facade: '',
  streetWidthM: '',
  rentPeriod: '',
  tenantType: '',
  titleAr: '',
  price: '',
  descriptionAr: '',
};

type Form = typeof blank;

/** Everything the form holds, as one value: what the draft stores and what an edit loads. */
interface Snapshot {
  form: Form;
  pin: LatLng | null;
  features: Record<string, boolean>;
  photos: string[];
  windows: Window[];
}

/**
 * A new property in progress lives in the browser until it is saved, so a reload, a crash or a closed
 * tab costs nothing. Before, it all went — and the photos already uploaded stayed in the bucket with
 * nothing pointing at them. Kept per account (and per broker an admin is acting for), for three days:
 * the server sweeps uploads nothing has claimed after seven, so a draft never outlives its photos.
 */
const DRAFT_TTL_MS = 3 * 86_400_000;
const draftKey = (userId: string | undefined) => `wasity.addProperty.draft:${actAs.get()?.id ?? userId ?? 'anon'}`;
function readDraft(key: string): Snapshot | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const d = JSON.parse(raw) as Snapshot & { savedAt: number };
    if (!d.savedAt || Date.now() - d.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return { form: { ...blank, ...d.form }, pin: d.pin ?? null, features: d.features ?? {}, photos: d.photos ?? [], windows: d.windows ?? DEFAULT_WINDOWS };
  } catch {
    return null;
  }
}
function writeDraft(key: string, s: Snapshot | null) {
  try {
    if (s) localStorage.setItem(key, JSON.stringify({ ...s, savedAt: Date.now() }));
    else localStorage.removeItem(key);
  } catch {
    /* private mode or full: the form still works, it just is not kept */
  }
}
const BLANK: Snapshot = { form: blank, pin: null, features: {}, photos: [], windows: DEFAULT_WINDOWS };
/** Nothing typed, picked or pinned: not worth keeping. */
const untouched = (s: Snapshot) => !s.pin && !s.photos.length && !Object.values(s.features).some(Boolean) && (Object.keys(blank) as (keyof Form)[]).every((k) => s.form[k] === blank[k]);

const str = (n: number | null | undefined) => (n == null ? '' : String(n));
/** A saved property back into the form's strings, for editing. */
function snapshotOf(p: BrokerProperty, windows: Window[]): Snapshot {
  const l = p.listing;
  return {
    form: {
      purpose: p.purpose,
      type: p.type,
      city: p.city,
      district: p.district,
      street: p.street ?? '',
      areaM2: str(p.areaM2),
      bedrooms: str(p.bedrooms),
      bathrooms: str(p.bathrooms),
      livingRooms: str(p.livingRooms),
      floors: str(p.floors),
      floorNo: str(p.floorNo),
      units: str(p.units),
      shops: str(p.shops),
      landUse: p.landUse ?? '',
      ageYears: str(p.ageYears),
      facade: p.facade ?? '',
      streetWidthM: str(p.streetWidthM),
      rentPeriod: p.rentPeriod ?? '',
      tenantType: p.tenantType ?? '',
      titleAr: l?.titleAr ?? '',
      price: l ? String(l.priceHalalas / 100) : '',
      descriptionAr: l?.descriptionAr ?? '',
    },
    pin: { lat: p.lat, lng: p.lng },
    features: p.features ?? {},
    photos: l?.photoMediaIds.length ? l.photoMediaIds : p.photoMediaIds,
    windows,
  };
}

/** Where the API's path for a field lands in this form, when the names differ. */
const fieldOf = (path: string) => (path.startsWith('property.location') || path.startsWith('location') ? 'pin' : path.endsWith('priceHalalas') ? 'price' : path.endsWith('photoMediaIds') ? 'photos' : undefined);

const num = (v: string) => {
  const n = Number(v.replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d))).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) && v.trim() !== '' ? n : undefined;
};

/**
 * A labelled input whose label actually points at it. `Field` renders the two as siblings, so without
 * `htmlFor` a screen reader lands on an unnamed box — and nothing can find it by its label either.
 */
function Text({ id, label, value, onChange, error, ...rest }: { id: string; label: string; value: string; onChange: (v: string) => void; error?: string } & Omit<React.ComponentProps<typeof Input>, 'value' | 'onChange' | 'id'>) {
  return (
    <Field label={label} htmlFor={id} error={error}>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={error ? true : undefined} {...rest} />
    </Field>
  );
}

function Choice({ id, label, value, onChange, error, children }: { id: string; label: string; value: string; onChange: (v: string) => void; error?: string; children: React.ReactNode }) {
  return (
    <Field label={label} htmlFor={id} error={error}>
      <Select id={id} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={error ? true : undefined}>{children}</Select>
    </Field>
  );
}

export function AddProperty() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const session = useSession();
  const key = draftKey(session.data?.user.id);
  const canPublish = usePublishAllowed();
  // Read once, on the first render: what was being entered when the page last closed.
  const [restored] = useState(() => readDraft(key));
  const begin = restored ?? BLANK;
  const [form, setForm] = useState<Form>(begin.form);
  const [pin, setPin] = useState<LatLng | null>(begin.pin);
  const [features, setFeatures] = useState<Record<string, boolean>>(begin.features);
  const [photos, setPhotos] = useState<string[]>(begin.photos);
  const [windows, setWindows] = useState<Window[]>(begin.windows);
  const [draftNotice, setDraftNotice] = useState(!!restored);
  /** The saved property being edited; null while a new one is being added. */
  const [target, setTarget] = useState<BrokerProperty | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [editing, setEditing] = useState<{ id: string; windows: Window[] } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<BrokerProperty | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof Form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const load = (s: Snapshot) => {
    setForm(s.form);
    setPin(s.pin);
    setFeatures(s.features);
    setPhotos(s.photos);
    setWindows(s.windows);
  };

  // The draft follows every change to a new property. An edit is never written over it, so a new
  // property half-entered before the edit is still there after it.
  useEffect(() => {
    if (target) return;
    const snap = { form, pin, features, photos, windows };
    writeDraft(key, untouched(snap) ? null : snap);
  }, [key, target, form, pin, features, photos, windows]);

  const mine = useQuery({ queryKey: ['broker', 'properties'], queryFn: () => api.get<{ items: BrokerProperty[] }>('/api/broker/properties') });

  const propertyBody = () => ({
    purpose: form.purpose,
    type: form.type,
    city: form.city.trim(),
    district: form.district.trim(),
    street: form.street.trim() || undefined,
    location: { lat: pin!.lat, lng: pin!.lng },
    areaM2: num(form.areaM2),
    bedrooms: num(form.bedrooms),
    bathrooms: num(form.bathrooms),
    livingRooms: num(form.livingRooms),
    floors: num(form.floors),
    floorNo: num(form.floorNo),
    units: num(form.units),
    shops: num(form.shops),
    landUse: form.landUse || undefined,
    ageYears: num(form.ageYears),
    facade: form.facade || undefined,
    streetWidthM: num(form.streetWidthM),
    features: Object.fromEntries(Object.entries(features).filter(([, on]) => on)),
    rentPeriod: form.purpose === 'rent' ? form.rentPeriod || undefined : undefined,
    tenantType: form.purpose === 'rent' ? form.tenantType || undefined : undefined,
    photoMediaIds: photos,
  });
  const listingBody = () => ({
    titleAr: form.titleAr.trim(),
    priceHalalas: Math.round((num(form.price) ?? 0) * 100),
    descriptionAr: form.descriptionAr.trim() || undefined,
    photoMediaIds: photos,
  });

  const save = useMutation({
    mutationFn: async (): Promise<{ propertyId: string; edited: boolean }> => {
      if (target) {
        // The property and its advert are one form here and two rows on the server; both are written,
        // then the viewing hours, each idempotent so a failed step can simply be saved again.
        await api.patch(`/api/broker/properties/${target.id}`, propertyBody());
        if (target.listing) await api.patch(`/api/broker/listings/${target.listing.id}`, listingBody());
        await api.put(`/api/broker/properties/${target.id}/availability`, { slots: windows });
        return { propertyId: target.id, edited: true };
      }
      const created = await api.post<{ property: { id: string } }>('/api/broker/properties', { property: propertyBody(), listing: listingBody() });
      return { propertyId: created.property.id, edited: false };
    },
    // On a new property the windows need its id, so they follow the create rather than riding with it.
    // A property saved without them is still a property; it simply cannot be booked until they are set,
    // which is what the warning under the editor says.
    onSuccess: async ({ propertyId, edited }) => {
      if (!edited && windows.length) await api.put(`/api/broker/properties/${propertyId}/availability`, { slots: windows });
      if (!edited) writeDraft(key, null);
      setTarget(null);
      load(edited ? (readDraft(key) ?? BLANK) : BLANK);
      setSaveError(null);
      setError(null);
      setDraftNotice(false);
      setSavedNotice(edited);
      void qc.invalidateQueries({ queryKey: ['broker', 'properties'] });
      void qc.invalidateQueries({ queryKey: ['broker', 'listings'] });
    },
    onError: (e) => setSaveError(e),
  });
  const errs = fieldErrors(saveError, t, fieldOf);

  const startEdit = async (p: BrokerProperty) => {
    try {
      const got = await api.get<{ slots: Window[] }>(`/api/broker/properties/${p.id}/availability`);
      setTarget(p);
      load(snapshotOf(p, got.slots));
      setSaveError(null);
      setError(null);
      setDraftNotice(false);
      setSavedNotice(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(errorText(e, t));
    }
  };
  const cancelEdit = () => {
    setTarget(null);
    load(readDraft(key) ?? BLANK);
    setSaveError(null);
  };
  const discardDraft = () => {
    writeDraft(key, null);
    load(BLANK);
    setDraftNotice(false);
  };

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/broker/properties/${id}`),
    onSuccess: (_r, id) => {
      setConfirmRemove(null);
      if (target?.id === id) cancelEdit();
      void qc.invalidateQueries({ queryKey: ['broker', 'properties'] });
    },
    onError: (e) => setError(errorText(e, t)),
  });

  /**
   * Upload what was picked, keep whatever lands, and say what did not.
   *
   * Picking thirteen photos used to add ten and drop three without a word — and one failure in the
   * middle threw the whole batch away, leaving the ones already uploaded in the bucket with nothing
   * pointing at them.
   */
  const addPhotos = async (files: FileList | null) => {
    if (!files?.length) return;
    const picked = Array.from(files);
    const room = Math.max(MAX_LISTING_PHOTOS - photos.length, 0);
    setUploading(true);
    setError(null);
    let failed = 0;
    let lastError: unknown = null;
    for (const f of picked.slice(0, room)) {
      try {
        const { mediaId } = await uploadFile('/api/broker/media', f, 'listing_photo');
        setPhotos((p) => [...p, mediaId].slice(0, MAX_LISTING_PHOTOS));
      } catch (e) {
        failed++;
        lastError = e;
      }
    }
    const over = picked.length - room;
    setUploading(false);
    if (failed) setError([t('broker.listings.photosFailed', { failed, total: picked.length }), specificErrorText(lastError, t)].filter(Boolean).join(' '));
    else if (over > 0) setError(t('broker.listings.photosOverCap', { over, max: MAX_LISTING_PHOTOS }));
  };

  const openWindows = async (id: string) => {
    try {
      const got = await api.get<{ slots: Window[] }>(`/api/broker/properties/${id}/availability`);
      setEditing({ id, windows: got.slots });
    } catch (e) {
      setError(errorText(e, t));
    }
  };
  const saveWindows = useMutation({
    mutationFn: () => api.put(`/api/broker/properties/${editing!.id}/availability`, { slots: editing!.windows }),
    onSuccess: () => setEditing(null),
    onError: (e) => setError(errorText(e, t)),
  });

  const rooms = ROOMED.includes(form.type);
  const built = BUILT.includes(form.type);
  const missing: string[] = [];
  if (!pin) missing.push(t('broker.addProperty.whereIsIt'));
  if (!form.district.trim()) missing.push(t('app.district'));
  if (!num(form.areaM2)) missing.push(t('broker.addProperty.areaM2'));
  if (RESIDENTIAL.includes(form.type)) {
    if (num(form.bedrooms) === undefined) missing.push(t('broker.addProperty.bedrooms'));
    if (num(form.bathrooms) === undefined) missing.push(t('broker.addProperty.bathrooms'));
    if (num(form.livingRooms) === undefined) missing.push(t('broker.addProperty.livingRooms'));
  }
  if (form.type === 'building' && num(form.units) === undefined) missing.push(t('broker.addProperty.units'));
  if (form.type === 'land' && !form.landUse) missing.push(t('broker.addProperty.landUse'));
  if (form.purpose === 'rent' && !form.rentPeriod) missing.push(t('broker.addProperty.rentPeriod'));
  if (form.titleAr.trim().length < 3) missing.push(t('broker.addProperty.titleAr'));
  if (!((num(form.price) ?? 0) > 0)) missing.push(t('broker.listings.price'));
  if (!windowsValid(windows)) missing.push(t('broker.windows.title'));
  const ready = missing.length === 0;

  return (
    <>
      <PageHeader
        crumbs={[{ to: '/broker', label: t('nav.dashboard') }, { label: t('nav.addProperty') }]}
        title={target ? t('broker.addProperty.editTitle', { number: target.listing?.number ?? '' }) : t('nav.addProperty')}
        meta={target ? t('broker.addProperty.editIntro') : t('broker.addProperty.intro')}
      />

      <div className="flex flex-col gap-3 mb-4 empty:hidden">
        {!canPublish ? <MembershipNeededNotice /> : null}
        {draftNotice && !target ? (
          <Notice tone="info">
            {t('broker.addProperty.draftRestored')}{' '}
            <Button variant="text" className="text-[14px] inline-flex min-h-0 p-0" onClick={discardDraft}>{t('broker.addProperty.discardDraft')}</Button>
          </Notice>
        ) : null}
        {savedNotice ? <Notice tone="success">{t('broker.addProperty.saved')}</Notice> : null}
        {saveError ? <Notice tone="error">{errorText(saveError, t)}</Notice> : null}
        {error ? <Notice tone="error">{error}</Notice> : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="flex flex-col gap-3">
          <h3>{t('broker.addProperty.whereIsIt')}</h3>
          <p className="text-[13px] text-muted">{t('broker.addProperty.whereHint')}</p>
          <MapPicker value={pin} onChange={setPin} />
          {errs.pin ? <p className="text-[13px] text-error">{errs.pin}</p> : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <Text id="f-city" error={errs.city} label={t('app.city')} value={form.city} onChange={(v) => set('city', v)} />
            <Text id="f-district" error={errs.district} label={t('app.district')} value={form.district} onChange={(v) => set('district', v)} placeholder={t('broker.addProperty.districtPlaceholder')} />
          </div>
          <Text id="f-street" error={errs.street} label={t('broker.addProperty.street')} value={form.street} onChange={(v) => set('street', v)} />
        </Card>

        <Card className="flex flex-col gap-3">
          <h3>{t('broker.addProperty.basics')}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Choice id="f-purpose" error={errs.purpose} label={t('broker.addProperty.purpose')} value={form.purpose} onChange={(v) => set('purpose', v)}>
                {PROPERTY_PURPOSES.map((p) => <option key={p} value={p}>{t(`broker.addProperty.purposes.${p}`)}</option>)}
              </Choice>
            <Choice id="f-type" error={errs.type} label={t('app.kind')} value={form.type} onChange={(v) => set('type', v)}>
                {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{PROPERTY_TYPE_LABELS[p][locale]}</option>)}
              </Choice>
            <Text id="f-areaM2" error={errs.areaM2} label={t('broker.addProperty.areaM2')} value={form.areaM2} onChange={(v) => set('areaM2', v)} inputMode="decimal" />
            <Text id="f-price" error={errs.price} label={t('broker.listings.price')} value={form.price} onChange={(v) => set('price', v)} inputMode="numeric" />
          </div>
          {form.purpose === 'rent' ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Choice id="f-rentPeriod" error={errs.rentPeriod} label={t('broker.addProperty.rentPeriod')} value={form.rentPeriod} onChange={(v) => set('rentPeriod', v)}>
                  <option value="">{t('app.none')}</option>
                  {RENT_PERIODS.map((p) => <option key={p} value={p}>{t(`broker.addProperty.rentPeriods.${p}`)}</option>)}
                </Choice>
              <Choice id="f-tenantType" error={errs.tenantType} label={t('broker.addProperty.tenantType')} value={form.tenantType} onChange={(v) => set('tenantType', v)}>
                  <option value="">{t('app.none')}</option>
                  {TENANT_TYPES.map((p) => <option key={p} value={p}>{t(`broker.addProperty.tenantTypes.${p}`)}</option>)}
                </Choice>
            </div>
          ) : null}
          {form.type === 'land' ? (
            <Choice id="f-landUse" error={errs.landUse} label={t('broker.addProperty.landUse')} value={form.landUse} onChange={(v) => set('landUse', v)}>
                <option value="">{t('app.none')}</option>
                {LAND_USES.map((p) => <option key={p} value={p}>{t(`broker.addProperty.landUses.${p}`)}</option>)}
              </Choice>
          ) : null}
        </Card>

        <Card className="flex flex-col gap-3">
          <h3>{t('broker.addProperty.details')}</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            {rooms ? <Text id="f-bedrooms" error={errs.bedrooms} label={t('broker.addProperty.bedrooms')} value={form.bedrooms} onChange={(v) => set('bedrooms', v)} inputMode="numeric" /> : null}
            {rooms ? <Text id="f-bathrooms" error={errs.bathrooms} label={t('broker.addProperty.bathrooms')} value={form.bathrooms} onChange={(v) => set('bathrooms', v)} inputMode="numeric" /> : null}
            {rooms ? <Text id="f-livingRooms" error={errs.livingRooms} label={t('broker.addProperty.livingRooms')} value={form.livingRooms} onChange={(v) => set('livingRooms', v)} inputMode="numeric" /> : null}
            {/* A plot of land has no storeys, no floor number and no age; a farm is the same. The form
                already hides the room counts for them and then asked for these three anyway. */}
            {built ? <Text id="f-floors" error={errs.floors} label={t('broker.addProperty.floors')} value={form.floors} onChange={(v) => set('floors', v)} inputMode="numeric" /> : null}
            {built ? <Text id="f-floorNo" error={errs.floorNo} label={t('broker.addProperty.floorNo')} value={form.floorNo} onChange={(v) => set('floorNo', v)} inputMode="numeric" /> : null}
            {built ? <Text id="f-ageYears" error={errs.ageYears} label={t('broker.addProperty.ageYears')} value={form.ageYears} onChange={(v) => set('ageYears', v)} inputMode="numeric" /> : null}
            {form.type === 'building' ? <Text id="f-units" error={errs.units} label={t('broker.addProperty.units')} value={form.units} onChange={(v) => set('units', v)} inputMode="numeric" /> : null}
            {form.type === 'building' ? <Text id="f-shops" error={errs.shops} label={t('broker.addProperty.shops')} value={form.shops} onChange={(v) => set('shops', v)} inputMode="numeric" /> : null}
            <Text id="f-streetWidthM" error={errs.streetWidthM} label={t('broker.addProperty.streetWidthM')} value={form.streetWidthM} onChange={(v) => set('streetWidthM', v)} inputMode="decimal" />
            <Choice id="f-facade" error={errs.facade} label={t('broker.addProperty.facade')} value={form.facade} onChange={(v) => set('facade', v)}>
                <option value="">{t('app.none')}</option>
                {FACADES.map((p) => <option key={p} value={p}>{t(`broker.addProperty.facades.${p}`)}</option>)}
              </Choice>
          </div>
          <h3 className="mt-2">{t('broker.addProperty.features')}</h3>
          <div className="flex flex-wrap gap-2">
            {FEATURES.map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={!!features[f]}
                onClick={() => setFeatures((s) => ({ ...s, [f]: !s[f] }))}
                className={`min-h-9 px-3 rounded-md border text-[14px] ${features[f] ? 'bg-accent text-accent-fg border-accent' : 'border-input-line text-fg-2 hover:text-fg'}`}
              >
                {t(`broker.addProperty.featureNames.${f}`)}
              </button>
            ))}
          </div>
        </Card>

        <Card className="flex flex-col gap-3">
          <h3>{t('broker.addProperty.advert')}</h3>
          <Text id="f-titleAr" error={errs.titleAr} label={t('broker.addProperty.titleAr')} value={form.titleAr} onChange={(v) => set('titleAr', v)} placeholder={t('broker.addProperty.titlePlaceholder')} />
          <Field label={t('broker.addProperty.descriptionAr')} htmlFor="f-descriptionAr" error={errs.descriptionAr}><Textarea id="f-descriptionAr" rows={4} value={form.descriptionAr} onChange={(e) => set('descriptionAr', e.target.value)} /></Field>
          <div className="flex flex-col gap-2">
            <span className="text-[13px] text-muted">{t('broker.addProperty.photos')}</span>
            <div className="flex flex-wrap items-center gap-2">
              {photos.map((id, i) => (
                <span key={id} className="inline-flex items-center gap-1.5 min-h-9 px-3 rounded-md border border-line text-[13px]">
                  {i === 0 ? t('broker.addProperty.cover') : `#${i + 1}`}
                  <button type="button" aria-label={t('app.remove')} onClick={() => setPhotos((p) => p.filter((x) => x !== id))} className="text-muted hover:text-error"><X size={14} /></button>
                </span>
              ))}
              <label className="inline-flex items-center gap-2 min-h-9 px-3 rounded-md border border-input-line text-[14px] cursor-pointer hover:text-fg text-fg-2 focus-within:border-accent focus-within:shadow-[var(--shadow-focus)]">
                {uploading ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <Upload size={15} aria-hidden />}
                {uploading ? t('broker.addProperty.uploading') : t('broker.addProperty.addPhotos')}
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="sr-only" disabled={uploading} onChange={(e) => void addPhotos(e.target.files)} />
              </label>
            </div>
            <p className={`text-[13px] ${errs.photos ? 'text-error' : 'text-muted'}`}>{errs.photos ?? t('broker.addProperty.photosHint')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" className="flex-1" disabled={!ready || save.isPending || uploading} onClick={() => save.mutate()}>
              {save.isPending ? <Loader2 size={16} className="animate-spin" aria-hidden /> : target ? <Pencil size={16} aria-hidden /> : <Plus size={16} aria-hidden />}
              {target ? t('broker.addProperty.saveChanges') : t('broker.addProperty.save')}
            </Button>
            {target ? <Button variant="secondary" disabled={save.isPending} onClick={cancelEdit}>{t('broker.addProperty.cancelEdit')}</Button> : null}
          </div>
          {!ready ? <p className="text-[13px] text-muted">{t('broker.addProperty.needed')} {missing.join(locale === 'ar' ? '، ' : ', ')}</p> : null}
        </Card>

        <Card className="flex flex-col gap-3">
          <h3>{t('broker.windows.title')}</h3>
          <p className="text-[13px] text-muted">{t('broker.windows.hint')}</p>
          <ViewingWindows value={windows} onChange={setWindows} />
        </Card>
      </div>

      <h2 className="mt-8 mb-3">{t('broker.addProperty.mine')}</h2>
      {mine.isLoading ? <Skeleton lines={4} /> : null}
      {mine.error ? <ErrorNotice error={mine.error} retry={() => void mine.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} /> : null}
      {mine.data && mine.data.items.length === 0 ? <EmptyState title={t('broker.addProperty.noneYet')} /> : null}
      {mine.data && mine.data.items.length > 0 ? (
        <Table>
          <thead><tr><th>{t('app.number')}</th><th>{t('app.property')}</th><th>{t('broker.listings.price')}</th><th>{t('app.status')}</th><th /></tr></thead>
          <tbody>
            {mine.data.items.map((p) => (
              <tr key={p.id} className={target?.id === p.id ? 'bg-bg-2' : ''}>
                <td className="num">{p.listing?.number ?? '—'}</td>
                <td>{PROPERTY_TYPE_LABELS[p.type][locale]} · {p.district}</td>
                <td className="num">{p.listing ? (p.listing.priceHalalas / 100).toLocaleString('en-US') : '—'}</td>
                <td>{p.listing ? <ListingChip l={p.listing} /> : null}</td>
                <td className="text-end whitespace-nowrap">
                  <Button variant="text" className="text-[14px]" disabled={target?.id === p.id} onClick={() => void startEdit(p)}>
                    <Pencil size={14} aria-hidden /> {t('broker.addProperty.edit')}
                  </Button>
                  <Button variant="text" className="text-[14px]" onClick={() => void openWindows(p.id)}>
                    <Clock size={14} aria-hidden /> {t('broker.windows.edit')}
                  </Button>
                  <Button variant="text" className="text-[14px] text-error" disabled={remove.isPending} onClick={() => setConfirmRemove(p)}>
                    <Trash2 size={14} aria-hidden /> {t('app.remove')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}

      <ConfirmDialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        title={t('broker.addProperty.removeTitle')}
        body={t('broker.addProperty.removeBody', { number: confirmRemove?.listing?.number ?? '' })}
        confirmLabel={t('app.remove')}
        cancelLabel={t('app.back')}
        danger
        loading={remove.isPending}
        onConfirm={() => confirmRemove && remove.mutate(confirmRemove.id)}
      />

      {editing ? (
        <Dialog open title={t('broker.windows.title')} onClose={() => setEditing(null)}>
          <p className="text-[13px] text-muted mb-3">{t('broker.windows.hint')}</p>
          <ViewingWindows value={editing.windows} onChange={(w) => setEditing({ ...editing, windows: w })} />
          <div className="flex gap-2 mt-4">
            <Button variant="primary" disabled={!windowsValid(editing.windows) || saveWindows.isPending} onClick={() => saveWindows.mutate()}>{t('app.save')}</Button>
            <Button variant="secondary" onClick={() => setEditing(null)}>{t('app.back')}</Button>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
